// src/components/notification-list.jsx
import { useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import {
  CheckCircle,
  AlertCircle,
  Info,
  Trash2,
  FileText,
  User,
  Package,
  Shield,
} from "lucide-react";
import { toast } from "react-toastify";
import { markAsRead } from "../../../../Services/notificationService";

export function NotificationList({ notifications, onRefresh }) {
  const [expandedNotification, setExpandedNotification] = useState(null);

  const getIcon = (resourceType, action) => {
    switch (resourceType) {
      case "FORM":
        return <FileText className="h-5 w-5 text-purple-500" />;
      case "MATERIAL":
        return <Package className="h-5 w-5 text-amber-500" />;
      case "USER":
        return <User className="h-5 w-5 text-green-500" />;
      case "ADMIN":
        return <Shield className="h-5 w-5 text-red-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "CREATE":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "DELETE":
        return <Trash2 className="h-5 w-5 text-red-500" />;
      case "UPDATE":
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  const toggleExpand = (id) => {
    setExpandedNotification(expandedNotification === id ? null : id);
  };

  const handleMarkAsRead = async (id) => {
    try {
      await markAsRead(id);
      onRefresh(); // Refresh the list
    } catch (error) {
      console.error("Error marking notification as read:", error);
      toast.error("Failed to mark notification as read");
    }
  };

  return (
    <div className="space-y-3">
      {notifications.map((notification) => (
        <div
          key={notification._id}
          className={`border rounded-lg transition-all duration-200 overflow-hidden ${
            notification.isRead ? "bg-white" : "bg-blue-50 border-blue-200"
          }`}
        >
          <div
            className="p-4 cursor-pointer flex items-start"
            onClick={() => toggleExpand(notification._id)}
          >
            <div className="mr-3 mt-1">
              {getIcon(notification.resourceType)}
            </div>
            <div className="flex-grow min-w-0">
              <div className="flex items-start justify-between mb-1">
                <h3
                  className={`font-medium text-gray-900 truncate mr-2 ${
                    !notification.isRead ? "font-semibold" : ""
                  }`}
                >
                  {notification.title}
                </h3>
                <div className="flex items-center shrink-0">
                  {getActionIcon(notification.action)}
                  <span className="text-xs text-gray-500 ml-2">
                    {formatDistanceToNow(new Date(notification.createdAt), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
              </div>
              <p className="text-gray-600 text-sm line-clamp-2">
                {notification.message}
              </p>
              <div className="mt-2 flex items-center text-xs">
                <span
                  className="px-2 py-1 rounded-full font-medium mr-2"
                  style={{
                    backgroundColor:
                      notification.resourceType === "FORM"
                        ? "rgba(147, 51, 234, 0.1)"
                        : notification.resourceType === "MATERIAL"
                        ? "rgba(245, 158, 11, 0.1)"
                        : notification.resourceType === "USER"
                        ? "rgba(16, 185, 129, 0.1)"
                        : notification.resourceType === "ADMIN"
                        ? "rgba(239, 68, 68, 0.1)"
                        : "rgba(59, 130, 246, 0.1)",
                    color:
                      notification.resourceType === "FORM"
                        ? "rgb(147, 51, 234)"
                        : notification.resourceType === "MATERIAL"
                        ? "rgb(245, 158, 11)"
                        : notification.resourceType === "USER"
                        ? "rgb(16, 185, 129)"
                        : notification.resourceType === "ADMIN"
                        ? "rgb(239, 68, 68)"
                        : "rgb(59, 130, 246)",
                  }}
                >
                  {notification.resourceType}
                </span>
                <span
                  className="px-2 py-1 rounded-full font-medium"
                  style={{
                    backgroundColor:
                      notification.action === "CREATE"
                        ? "rgba(16, 185, 129, 0.1)"
                        : notification.action === "DELETE"
                        ? "rgba(239, 68, 68, 0.1)"
                        : notification.action === "UPDATE"
                        ? "rgba(245, 158, 11, 0.1)"
                        : "rgba(59, 130, 246, 0.1)",
                    color:
                      notification.action === "CREATE"
                        ? "rgb(16, 185, 129)"
                        : notification.action === "DELETE"
                        ? "rgb(239, 68, 68)"
                        : notification.action === "UPDATE"
                        ? "rgb(245, 158, 11)"
                        : "rgb(59, 130, 246)",
                  }}
                >
                  {notification.action}
                </span>
              </div>
            </div>
          </div>
          {expandedNotification === notification._id && (
            <div className="px-4 pb-4 pt-1 border-t border-gray-100">
              <div className="text-sm text-gray-700 mb-3">
                <p className="mb-2">
                  <strong>From:</strong>{" "}
                  {notification.createdBy?.name || "System"}
                </p>
                <p className="mb-2">
                  <strong>Date:</strong>{" "}
                  {format(new Date(notification.createdAt), "PPpp")}
                </p>
                {notification.metadata &&
                  Object.keys(notification.metadata).length > 0 && (
                    <div className="mt-2">
                      <p>
                        <strong>Additional Information:</strong>
                      </p>
                      <pre className="bg-gray-50 p-2 rounded-md mt-1 text-xs overflow-auto">
                        {JSON.stringify(notification.metadata, null, 2)}
                      </pre>
                    </div>
                  )}
              </div>
              <div className="flex justify-end space-x-2">
                {!notification.isRead && (
                  <button
                    className="px-3 py-1 text-xs bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsRead(notification._id);
                    }}
                  >
                    Mark as Read
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
